import { Dropdown, Icon, Input } from "@screencloud/screencloud-ui-components";
import type { ReactNode } from "react";
import { type InjectedIntlProps, injectIntl } from "react-intl";
import { Constants } from "src/billinglatest/constants";
import { util } from "src/billinglatest/util";
import type { PixiAddress } from "../../hooks/usePixiWizardState";
import { countryHasState, type AddressErrorReason } from "./utils/validation";
import { ErrorText, Field, FieldGroup, FieldRow, FormBody } from "./styles";

const COUNTRY_OPTIONS = util.object.toDropdown(Constants.Countries);

type AddressFieldName = keyof PixiAddress;

export interface AddressFormProps {
  values: PixiAddress;
  /** Billing collects a state field; shipping does not. */
  includeState: boolean;
  testIdPrefix: string;
  onChange: (field: AddressFieldName, value: string) => void;
  onBlur: (field: AddressFieldName) => void;
  error: (field: AddressFieldName) => AddressErrorReason | undefined;
  /** Rendered between the contact and address groups (unsupported warning). */
  addressSlot?: ReactNode;
  /** Rendered as the last row of the form (e.g. the shipping-method dropdown). */
  footerSlot?: ReactNode;
  /** When true, the address inputs are read-only (footerSlot is unaffected). */
  disabled?: boolean;
}

const AddressFormBase = ({
  values,
  includeState,
  testIdPrefix,
  onChange,
  onBlur,
  error,
  addressSlot,
  footerSlot,
  disabled,
  intl,
}: AddressFormProps & InjectedIntlProps) => {
  const tid = (suffix: string) => `${testIdPrefix}-${suffix}`;

  const placeholder = (id: string, defaultMessage: string) =>
    intl.formatMessage({ id, defaultMessage });

  const ERROR_MESSAGE: Record<
    AddressErrorReason,
    { id: string; defaultMessage: string }
  > = {
    required: {
      id: "pixi_wizard.payment_shipping.error.required",
      defaultMessage: "Please fill in this field.",
    },
    email_invalid: {
      id: "pixi_wizard.payment_shipping.error.email_invalid",
      defaultMessage: "Enter a valid email address.",
    },
    shipping_unsupported: {
      id: "pixi_wizard.payment_shipping.error.shipping_unsupported",
      defaultMessage: "Please choose a supported shipping country.",
    },
    postal_invalid: {
      id: "pixi_wizard.payment_shipping.error.postal_invalid",
      defaultMessage: "Enter a valid postal code for the selected country.",
    },
    phone_invalid: {
      id: "pixi_wizard.payment_shipping.error.phone_invalid",
      defaultMessage: "Enter a valid phone number.",
    },
    postal_mismatch: {
      id: "pixi_wizard.payment_shipping.error.postal_mismatch",
      defaultMessage: "This postal code doesn't match the address.",
    },
    state_mismatch: {
      id: "pixi_wizard.payment_shipping.error.state_mismatch",
      defaultMessage: "This state doesn't match the address.",
    },
    address_not_found: {
      id: "pixi_wizard.payment_shipping.error.address_not_found",
      defaultMessage:
        "We couldn't verify this address. Please check the details and try again.",
    },
  };

  const fieldError = (field: AddressFieldName): ReactNode => {
    const reason = error(field);
    return reason ? (
      <ErrorText data-testid={tid(`${field}-error`)} role="alert">
        <Icon name="warning" />
        {intl.formatMessage(ERROR_MESSAGE[reason])}
      </ErrorText>
    ) : null;
  };

  const textField = (
    field: AddressFieldName,
    labelId: string,
    label: string,
    type = "text",
  ) => (
    <Field>
      <Input
        fluid
        type={type}
        name={field}
        disabled={disabled}
        error={!!error(field)}
        placeholder={placeholder(labelId, label)}
        value={values[field]}
        onChange={(_event, data) => onChange(field, data.value)}
        onBlur={() => onBlur(field)}
        data-testid={tid(field)}
      />
      {fieldError(field)}
    </Field>
  );

  const stateOptions = countryHasState(values.country)
    ? util.object.toDropdown(
        Constants.States[values.country as keyof typeof Constants.States],
      )
    : [];

  return (
    <FormBody data-testid={tid("form")}>
      <FieldGroup>
        <FieldRow>
          {textField(
            "firstName",
            "pixi_wizard.payment_shipping.field.first_name",
            "First Name",
          )}
          {textField(
            "secondName",
            "pixi_wizard.payment_shipping.field.second_name",
            "Second Name",
          )}
        </FieldRow>
        {textField(
          "company",
          "pixi_wizard.payment_shipping.field.company",
          "Company Name",
        )}
        <FieldRow>
          {textField(
            "email",
            "pixi_wizard.payment_shipping.field.email",
            "Email",
            "email",
          )}
          {textField(
            "phone",
            "pixi_wizard.payment_shipping.field.phone",
            "Phone number",
            "tel",
          )}
        </FieldRow>
      </FieldGroup>

      {addressSlot}

      <FieldGroup>
        <FieldRow>
          <Field>
            <Dropdown
              fluid
              search
              selection
              disabled={disabled}
              name="country"
              error={!!error("country")}
              placeholder={placeholder(
                "pixi_wizard.payment_shipping.field.country",
                "Country",
              )}
              options={COUNTRY_OPTIONS}
              value={values.country}
              onChange={(_event, data) =>
                onChange("country", data.value as string)
              }
              onBlur={() => onBlur("country")}
              data-testid={tid("country")}
            />
            {fieldError("country")}
          </Field>
          {includeState && (
            <Field>
              {countryHasState(values.country) ? (
                <Dropdown
                  fluid
                  search
                  selection
                  disabled={disabled}
                  name="state"
                  error={!!error("state")}
                  placeholder={placeholder(
                    "pixi_wizard.payment_shipping.field.state",
                    "State",
                  )}
                  options={stateOptions}
                  value={values.state}
                  onChange={(_event, data) =>
                    onChange("state", data.value as string)
                  }
                  onBlur={() => onBlur("state")}
                  data-testid={tid("state")}
                />
              ) : (
                <Input
                  fluid
                  disabled={disabled}
                  name="state"
                  error={!!error("state")}
                  placeholder={placeholder(
                    "pixi_wizard.payment_shipping.field.state",
                    "State",
                  )}
                  value={values.state}
                  onChange={(_event, data) => onChange("state", data.value)}
                  onBlur={() => onBlur("state")}
                  data-testid={tid("state")}
                />
              )}
              {fieldError("state")}
            </Field>
          )}
        </FieldRow>
        {textField(
          "street",
          "pixi_wizard.payment_shipping.field.street",
          "Street Address",
        )}
        {textField(
          "building",
          "pixi_wizard.payment_shipping.field.building",
          "Building / Suite # / Apartment",
        )}
        <FieldRow>
          {textField("city", "pixi_wizard.payment_shipping.field.city", "City")}
          {textField(
            "postalCode",
            "pixi_wizard.payment_shipping.field.postal_code",
            "Postal Code",
          )}
        </FieldRow>
        {footerSlot}
      </FieldGroup>
    </FormBody>
  );
};

export const AddressForm = injectIntl(AddressFormBase);
